/**
 * Toggle a class on `root` when flex/grid children wrap to more than one row.
 * Measures in the un-stacked layout to avoid feedback loops.
 */
export class FlexWrapObserver {
    /**
     * @param {HTMLElement} root Element that receives the stacked class
     * @param {object} options
     * @param {string} [options.listSelector] List/container of items (default: root)
     * @param {string} [options.itemSelector='*'] Item selector within the list
     * @param {string} [options.stackedClass='is-wrapped'] Class toggled on root when wrapped
     */
    constructor(root, options = {}) {
        this.root = root;
        this.list = options.listSelector
            ? root.querySelector(options.listSelector)
            : root;
        this.itemSelector = options.itemSelector || ':scope > *';
        this.stackedClass = options.stackedClass || 'is-wrapped';
        this._raf = 0;

        if (!this.list) {
            return;
        }

        this.update = this.update.bind(this);
        this.scheduleUpdate = this.scheduleUpdate.bind(this);

        this.observer = new ResizeObserver(this.scheduleUpdate);
        this.observer.observe(this.list);
        window.addEventListener('resize', this.scheduleUpdate);

        this.update();
    }

    scheduleUpdate() {
        cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => this.update());
    }

    update() {
        if (!this.list) {
            return;
        }

        const wasStacked = this.root.classList.contains(this.stackedClass);
        this.root.classList.remove(this.stackedClass);

        const items = Array.from(this.list.querySelectorAll(this.itemSelector));
        if (items.length < 2) {
            if (wasStacked) {
                this.root.classList.remove(this.stackedClass);
            }
            return;
        }

        // Force layout after removing stacked styles.
        void this.list.offsetHeight;

        const firstTop = items[0].offsetTop;
        const wraps = items.some((item) => item.offsetTop !== firstTop);

        this.root.classList.toggle(this.stackedClass, wraps);
    }

    destroy() {
        cancelAnimationFrame(this._raf);
        this.observer?.disconnect();
        window.removeEventListener('resize', this.scheduleUpdate);
    }
}
